import { Claim } from "../api/model/Claim";
import { ClaimPhotoRecord } from "../api/model/ClaimPhoto";
import { ApiService } from "../api/ApiService";

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const CLAIM_FIELD_LABELS: Record<string, string> = {
    claimNumber: 'Claim #',
    incidentTime: 'Incident Time',
};

const EMPTY_CLAIM_GROUP: RecentClaimGroup = {
    mostRecentClaim: null,
    mostRecentClaimId: null,
    relatedClaimNumbers: [],
    relatedClaimIds: [],
};

export const CLAIM_PHOTO_RECIPIENTS = 'smanasrah@westernexp.com, scarney@westernexp.com, synergize@westernexp.com';

export interface ClaimDisplayField {
    key: string;
    label: string;
    value: string;
}

export interface RecentClaimGroup {
    mostRecentClaim: Claim | null;
    mostRecentClaimId: number | null;
    relatedClaimNumbers: string[];
    relatedClaimIds: number[];
}

export interface ClaimPhotoDriverInfo {
    name?: string;
    driverCode?: string;
    unit?: string;
    trailer?: string;
    division?: string;
    dmName?: string;
}

export interface ClaimPhotoEmailParams {
    relatedClaimNumbers: string[];
    photos: { data: string; webviewPath?: string }[];
    driver: ClaimPhotoDriverInfo;
    incidentTime?: string;
}

/** Sorts claims by incident time, newest first. */
const byIncidentTimeDesc = (a: Claim, b: Claim) =>
    new Date(b.incidentTime).getTime() - new Date(a.incidentTime).getTime();

/** Normalizes claim IDs from API values that may be strings or numbers. */
function normalizeClaimId(value: unknown): number | null {
    if (value == null || value === '') return null;
    const id = parseInt(String(value).replace(/\D/g, ''), 10);
    return Number.isNaN(id) ? null : id;
}

/** Formats a date as YYYY-MM-DD. */
function toApiDate(date: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Returns the date portion of an uploadDate value (YYYY-MM-DD). */
function uploadDateOnly(value: unknown): string {
    const match = String(value ?? '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
}

/** Returns driver/unit context and a 30-day date range for photo API calls. */
function photoQueryArgs(driverCode?: string, unitNumber?: string, claimId?: number) {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
        driverCode: driverCode?.toUpperCase() ?? '',
        unitNumber: unitNumber ?? '',
        claimId,
        startDate: toApiDate(start),
        endDate: toApiDate(end),
    };
}

/** Normalizes a photo record from the API and coerces accepted to a boolean. */
function normalizePhotoRecord(raw: ClaimPhotoRecord): ClaimPhotoRecord {
    return {
        ...raw,
        claimId: normalizeClaimId(raw.claimId) ?? raw.claimId,
        uploadDate: uploadDateOnly(raw.uploadDate),
        accepted: isPhotoAccepted(raw.accepted),
    };
}

/** Returns the newest photo record for a claim by table id (most recent insert). */
function latestPhotoRecord(records: ClaimPhotoRecord[], claimId: number): ClaimPhotoRecord | undefined {
    return records
        .filter(r => normalizeClaimId(r.claimId) === claimId)
        .sort((a, b) => b.id - a.id)[0];
}

/** Builds a claim group with numbers and IDs for the given related claims. */
function toClaimGroup(relatedClaims: Claim[], mostRecentClaim: Claim): RecentClaimGroup {
    return {
        mostRecentClaim,
        mostRecentClaimId: normalizeClaimId(mostRecentClaim.claimNumber),
        relatedClaimNumbers: relatedClaims.map(c => String(c.claimNumber ?? '').trim()).filter(Boolean),
        relatedClaimIds: relatedClaims
            .map(c => normalizeClaimId(c.claimNumber))
            .filter((id): id is number => id !== null),
    };
}
/** Coerces API accepted values (0, 1, boolean) into a boolean. */
function isPhotoAccepted(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
}

/** Converts a captured photo source into a JPEG blob, trying data then webview path. */
async function resolvePhotoBlob(photo: { data: string; webviewPath?: string }): Promise<Blob> {
    let lastError: unknown;
    for (const raw of [photo.data, photo.webviewPath].filter((v): v is string => !!v)) {
        try {
            const source = /^(data:|https?:|blob:)/.test(raw) ? raw : `data:image/jpeg;base64,${raw}`;
            const fetched = await fetch(source).then(r => r.blob());
            if (!fetched.size) throw new Error('Photo is empty');
            return new Blob([fetched], { type: 'image/jpeg' });
        } catch (error) {
            lastError = error;
        }
    }
    throw { errmsg: lastError instanceof Error ? lastError.message : 'Unable to read photo. Please retake it.' };
}

/** Returns display-ready label/value pairs for a claim, excluding status. */
export function getClaimDisplayFields(claim: Claim | null): ClaimDisplayField[] {
    if (!claim) return [];

    const label = (key: string) =>
        CLAIM_FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
    const value = (key: string, raw: string) => {
        if (!raw) return '—';
        if (key === 'incidentTime') {
            return new Date(raw).toLocaleString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
            });
        }
        return raw;
    };

    return (Object.keys(claim) as (keyof Claim)[])
        .filter(key => key !== 'status')
        .map(key => ({ key, label: label(key), value: value(key, String(claim[key] ?? '')) }));
}

/** Groups open claims from the last 30 days that fall within 12 hours of the newest incident. */
export function getRecentClaimGroup(claims: Claim[]): RecentClaimGroup {
    const isRecent = (incidentTime: string) => {
        const ts = new Date(incidentTime).getTime();
        return !Number.isNaN(ts) && ts <= Date.now() && Date.now() - ts <= THIRTY_DAYS_MS;
    };

    const openClaims = claims.filter(c => c.status === 'O' && c.incidentTime && isRecent(c.incidentTime));
    if (!openClaims.length) return EMPTY_CLAIM_GROUP;

    const mostRecentClaim = [...openClaims].sort(byIncidentTimeDesc)[0];
    const mostRecentTime = new Date(mostRecentClaim.incidentTime).getTime();
    const relatedClaims = openClaims.filter(c => {
        const ts = new Date(c.incidentTime).getTime();
        return ts <= mostRecentTime && mostRecentTime - ts <= TWELVE_HOURS_MS;
    });

    return toClaimGroup(relatedClaims, mostRecentClaim);
}

/** Returns whether the user still needs to submit photos (accepted is 0 or no record). */
export async function loadClaimPhotoStatus(
    page: string,
    relatedClaimIds: number[],
    mostRecentClaimId: number | null,
    driverCode?: string,
    unitNumber?: string,
): Promise<boolean> {
    if (!relatedClaimIds.length || mostRecentClaimId == null) return false;

    try {
        const records = (await ApiService.claimsPhotosGet(
            page,
            photoQueryArgs(driverCode, unitNumber, mostRecentClaimId),
        )).map(normalizePhotoRecord);

        const latest = latestPhotoRecord(records, mostRecentClaimId);
        return !latest?.accepted;
    } catch (error) {
        console.log('[ClaimsPhoto] loadClaimPhotoStatus error — treating photos as required', error);
        return true;
    }
}

/** Creates and marks accepted photo records for each claim ID. */
export async function postClaimPhotoRecords(
    page: string,
    claimIds: number[],
    numberOfPhotos: number,
    driverCode?: string,
    unitNumber?: string,
): Promise<void> {
    const context = {
        driverCode: driverCode?.toUpperCase() ?? '',
        unitNumber: unitNumber ?? '',
    };
    const uploadDate = toApiDate();
    for (const claimId of claimIds) {
        const id = await ApiService.claimsPhotosPost(page, { ...context, claimId, numberOfPhotos, uploadDate });
        await ApiService.claimsPhotosUpdate(page, { id, ...context, accepted: 1 });
    }
}

/** Builds a sanitized JPEG filename for a claim photo. */
export function buildClaimPhotoFilename(claimNumber: string, photoNumber: number): string {
    const safe = String(claimNumber).replace(/[^a-zA-Z0-9_-]/g, '_');
    return `${safe}_${photoNumber}.jpeg`;
}

/** Emails claim photos to the claims inbox for each related claim number. */
export async function sendClaimPhotoEmails({ relatedClaimNumbers, photos }: ClaimPhotoEmailParams): Promise<void> {
    const blobs = await Promise.all(photos.map(resolvePhotoBlob));
    for (const claimNumber of relatedClaimNumbers) {
        const files = Object.fromEntries(
            blobs.map((file, i) => [`file${i + 1}`, { fileName: buildClaimPhotoFilename(claimNumber, i + 1), file }]),
        );
        await ApiService.sendFileEmail('claims', { email: CLAIM_PHOTO_RECIPIENTS, subject: String(claimNumber) }, files);
    }
}

/** Loads claims and photo submission status for the claims page. */
export async function loadClaimsPageData(
    page: string,
    unitNumber?: string,
    driverCode?: string,
): Promise<{ claimGroup: RecentClaimGroup; photoSubmissionRequired: boolean }> {
    const claims = await ApiService.claimCheck(page, unitNumber);
    const claimGroup = getRecentClaimGroup(claims);
    const photoSubmissionRequired = await loadClaimPhotoStatus(
        page, claimGroup.relatedClaimIds, claimGroup.mostRecentClaimId, driverCode, unitNumber,
    );
    return { claimGroup, photoSubmissionRequired };
}
